import { useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, TextInput } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useTenant } from "@src/features/tenants";
import type { ScenarioRow } from "../utils/scenario-group";
import { useScenarioCoordinatesStore, EMPTY_SCENARIO_CONFIG } from "../stores/scenario-coordinates-store";
import { MapCanvas } from "./MapCanvas.web";

interface ScenarioMapProps {
  rows: ScenarioRow[];
  selectedScenarioUuid?: string | null;
  onScenarioSelect: (scenarioUuid: string) => void;
}

/**
 * Setup view: shared MapCanvas on top, editing chrome below. The map itself
 * (markers, click-to-place, tile fallback) lives in {@link MapCanvas}; this
 * wrapper handles the per-scenario configuration UI — friendly name,
 * lat/lng inputs, save/cancel/remove.
 */
export function ScenarioMap({ rows, selectedScenarioUuid, onScenarioSelect }: ScenarioMapProps) {
  const { theme } = useUnistyles();
  const { selectedTenant } = useTenant();
  const tenantId = selectedTenant?.id ?? "";
  const configByUuid = useScenarioCoordinatesStore((s) => s.byTenant[tenantId] ?? EMPTY_SCENARIO_CONFIG);
  const setCoordinate = useScenarioCoordinatesStore((s) => s.setCoordinate);
  const removeCoordinate = useScenarioCoordinatesStore((s) => s.removeCoordinate);
  const setFriendlyName = useScenarioCoordinatesStore((s) => s.setFriendlyName);

  const positioned = rows.filter((r) => configByUuid[r.scenarioUuid]?.coordinate);
  const unpositioned = rows.filter((r) => !configByUuid[r.scenarioUuid]?.coordinate);

  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [draftLat, setDraftLat] = useState("");
  const [draftLng, setDraftLng] = useState("");
  const [draftName, setDraftName] = useState("");

  const beginEdit = (row: ScenarioRow) => {
    setEditingUuid(row.scenarioUuid);
    const cfg = configByUuid[row.scenarioUuid];
    setDraftLat(cfg?.coordinate ? cfg.coordinate.lat.toFixed(6) : "");
    setDraftLng(cfg?.coordinate ? cfg.coordinate.lng.toFixed(6) : "");
    setDraftName(cfg?.friendlyName ?? "");
  };

  const cancelEdit = () => {
    setEditingUuid(null);
    setDraftLat("");
    setDraftLng("");
    setDraftName("");
  };

  const saveEdit = () => {
    if (!editingUuid || !tenantId) return;
    const latTrimmed = draftLat.trim();
    const lngTrimmed = draftLng.trim();
    const nameTrimmed = draftName.trim();
    const bothEmpty = latTrimmed === "" && lngTrimmed === "";
    const bothFilled = latTrimmed !== "" && lngTrimmed !== "";
    // Reject incomplete coord pair (one filled, one empty).
    if (!bothEmpty && !bothFilled) return;
    if (bothEmpty) {
      const existing = configByUuid[editingUuid];
      const hadCoordinate = existing?.coordinate !== undefined;
      const hadFriendlyName = (existing?.friendlyName ?? "").trim() !== "";
      // Bail BEFORE any store writes if nothing actually changed — the
      // form was already empty and the user clicked Save with no edits.
      // Without this guard, `setFriendlyName(..., "")` would still fire
      // a Zustand update + persist write, re-rendering map and list
      // subscribers for no reason.
      if (!hadCoordinate && !hadFriendlyName && nameTrimmed === "") return;
      // Clearing both coord fields is an explicit "remove pin" gesture.
      if (hadCoordinate) removeCoordinate(tenantId, editingUuid);
      // Always reflect the friendly name input, even when blank — clearing
      // the name field WITH the coords is an explicit "wipe everything"
      // gesture; if we skipped this when nameTrimmed === "" the override
      // would survive and the row would keep showing a stale display name
      // with no pin.
      setFriendlyName(tenantId, editingUuid, nameTrimmed);
      cancelEdit();
      return;
    }
    const lat = Number.parseFloat(latTrimmed);
    const lng = Number.parseFloat(lngTrimmed);
    if (Number.isNaN(lat) || Number.isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
    setCoordinate(tenantId, editingUuid, { lat, lng });
    setFriendlyName(tenantId, editingUuid, nameTrimmed);
    cancelEdit();
  };

  const editingRow = useMemo(
    () => (editingUuid ? (rows.find((r) => r.scenarioUuid === editingUuid) ?? null) : null),
    [editingUuid, rows],
  );

  // If the row being edited disappears from the rendered set (e.g. site filter
  // changed, scenario removed), the configure banner unmounts but the map's
  // crosshair would otherwise stay on. Clear the edit state so the map exits
  // click-to-place mode.
  useEffect(() => {
    if (editingUuid !== null && editingRow === null) cancelEdit();
  }, [editingUuid, editingRow]);

  // Stable callback so MapCanvas's click-handler effect doesn't re-register
  // the MapLibre `click` listener on every keystroke in the lat/lng/name
  // fields. setDraftLat / setDraftLng are React useState setters with stable
  // identity, so [] deps are correct.
  const handleMapClick = useCallback((lngLat: { lat: number; lng: number }) => {
    setDraftLat(lngLat.lat.toFixed(6));
    setDraftLng(lngLat.lng.toFixed(6));
  }, []);

  return (
    <View flex={1} style={{ minHeight: 0, gap: theme.spacing.lg }}>
      <MapCanvas
        rows={rows}
        selectedScenarioUuid={selectedScenarioUuid}
        onScenarioSelect={onScenarioSelect}
        onMapClick={editingUuid ? handleMapClick : undefined}
      />

      {/* Editing banner */}
      {editingRow && (
        <View
          style={{
            padding: theme.spacing["3xl"],
            borderRadius: theme.spacing.md,
            borderWidth: 1,
            borderColor: theme.colors.borderCard,
            backgroundColor: theme.colors.surfaceCard,
            gap: theme.spacing.lg,
          }}
        >
          <Text textStyle="sm/semibold" color="text">
            Configure — {editingRow.backendScenarioName}
          </Text>
          <Text textStyle="xs/regular" color="textMuted">
            Click anywhere on the map to drop a pin, or type latitude and longitude below. The friendly name is what
            shows everywhere in this app for this scenario.
          </Text>

          <View>
            <Text textStyle="xs/regular" color="textMuted">
              Friendly name
            </Text>
            <TextInput
              value={draftName}
              onChangeText={setDraftName}
              placeholder={editingRow.backendScenarioName}
              style={{
                borderWidth: 1,
                borderColor: theme.colors.borderCard,
                borderRadius: theme.spacing.md,
                paddingHorizontal: theme.spacing.lg,
                paddingVertical: theme.spacing.md,
                color: theme.colors.text,
              }}
            />
          </View>

          <View flexDirection="row" gap="lg">
            <View flex={1}>
              <Text textStyle="xs/regular" color="textMuted">
                Latitude
              </Text>
              <TextInput
                value={draftLat}
                onChangeText={setDraftLat}
                placeholder="-33.8688"
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderCard,
                  borderRadius: theme.spacing.md,
                  paddingHorizontal: theme.spacing.lg,
                  paddingVertical: theme.spacing.md,
                  color: theme.colors.text,
                }}
                inputMode="decimal"
              />
            </View>
            <View flex={1}>
              <Text textStyle="xs/regular" color="textMuted">
                Longitude
              </Text>
              <TextInput
                value={draftLng}
                onChangeText={setDraftLng}
                placeholder="151.2093"
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderCard,
                  borderRadius: theme.spacing.md,
                  paddingHorizontal: theme.spacing.lg,
                  paddingVertical: theme.spacing.md,
                  color: theme.colors.text,
                }}
                inputMode="decimal"
              />
            </View>
          </View>

          <View flexDirection="row" gap="lg" justifyContent="flex-end">
            <Pressable
              onPress={cancelEdit}
              style={{
                paddingHorizontal: theme.spacing["3xl"],
                paddingVertical: theme.spacing.md,
                borderWidth: 1,
                borderColor: theme.colors.borderCard,
                borderRadius: theme.spacing.md,
              }}
            >
              <Text textStyle="sm/regular" color="text">
                Cancel
              </Text>
            </Pressable>
            <Pressable
              onPress={saveEdit}
              style={{
                paddingHorizontal: theme.spacing["3xl"],
                paddingVertical: theme.spacing.md,
                backgroundColor: theme.colors.icon,
                borderRadius: theme.spacing.md,
              }}
            >
              <Text textStyle="sm/semibold" color="background">
                Save
              </Text>
            </Pressable>
          </View>
        </View>
      )}

      {/* Un-positioned list */}
      {unpositioned.length > 0 && (
        <View
          style={{
            maxHeight: 180,
            borderRadius: theme.spacing.md,
            borderWidth: 1,
            borderColor: theme.colors.borderCard,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              paddingHorizontal: theme.spacing.xl,
              paddingVertical: theme.spacing.md,
              backgroundColor: theme.colors.surfaceCard,
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderCard,
            }}
          >
            <Text textStyle="xs/semibold" color="textMuted">
              {unpositioned.length} scenario{unpositioned.length === 1 ? "" : "s"} need a pin
            </Text>
          </View>
          <ScrollView style={{ flex: 1 }}>
            {unpositioned.map((row) => (
              <View
                key={row.scenarioUuid}
                flexDirection="row"
                alignItems="center"
                style={{
                  paddingHorizontal: theme.spacing.xl,
                  paddingVertical: theme.spacing.lg,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.borderCard,
                  gap: theme.spacing.md,
                }}
              >
                <Icon name="motion" size={theme.spacing["4xl"]} color={theme.colors.textMuted} />
                <View flex={1}>
                  <Text textStyle="sm/regular" color="text" numberOfLines={1}>
                    {row.scenarioName}
                  </Text>
                  <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                    {row.cameraDisplayName}
                  </Text>
                </View>
                <Pressable
                  onPress={() => beginEdit(row)}
                  style={{
                    paddingHorizontal: theme.spacing.lg,
                    paddingVertical: theme.spacing.xs,
                    borderRadius: theme.spacing.md,
                    borderWidth: 1,
                    borderColor: theme.colors.borderCard,
                  }}
                >
                  <Text textStyle="xs/regular" color="text">
                    Configure
                  </Text>
                </Pressable>
              </View>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Positioned list */}
      {positioned.length > 0 && (
        <View
          style={{
            maxHeight: 160,
            borderRadius: theme.spacing.md,
            borderWidth: 1,
            borderColor: theme.colors.borderCard,
            overflow: "hidden",
          }}
        >
          <ScrollView style={{ flex: 1 }}>
            {positioned.map((row) => {
              const c = configByUuid[row.scenarioUuid]?.coordinate;
              if (!c) return null;
              return (
                <View
                  key={row.scenarioUuid}
                  flexDirection="row"
                  alignItems="center"
                  style={{
                    paddingHorizontal: theme.spacing.xl,
                    paddingVertical: theme.spacing.lg,
                    borderBottomWidth: 1,
                    borderBottomColor: theme.colors.borderCard,
                    gap: theme.spacing.md,
                    backgroundColor:
                      selectedScenarioUuid === row.scenarioUuid ? theme.colors.background : "transparent",
                  }}
                >
                  <Icon name="motion" size={theme.spacing["4xl"]} color={theme.colors.icon} />
                  <View flex={1}>
                    <Text textStyle="sm/regular" color="text" numberOfLines={1}>
                      {row.scenarioName}
                    </Text>
                    <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                      {c.lat.toFixed(4)}, {c.lng.toFixed(4)} · {row.cameraDisplayName}
                    </Text>
                  </View>
                  <Pressable onPress={() => beginEdit(row)} style={{ padding: theme.spacing.xs }}>
                    <Text textStyle="xs/regular" color="textMuted">
                      Edit
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={() => {
                      removeCoordinate(tenantId, row.scenarioUuid);
                      // If the user was mid-edit on this row, clear the
                      // draft state too — otherwise pressing Save next
                      // would re-write the deleted coordinate.
                      if (editingUuid === row.scenarioUuid) cancelEdit();
                    }}
                    style={{ padding: theme.spacing.xs }}
                  >
                    <Text textStyle="xs/regular" color="danger">
                      Remove
                    </Text>
                  </Pressable>
                </View>
              );
            })}
          </ScrollView>
        </View>
      )}
    </View>
  );
}
